<script>
  import { opsStatus, opsInFlight, tradingMode } from '../../stores.js'
  import { executeOpsCommand } from '../../api.js'
  import { OPS_ERROR_RU } from '../../lib/format.js'
  import { refreshCore } from '../../lib/polling.js'
  import {
    SITE_MODE,
    opsUnitsForSite,
    defaultOpsUnitForSite,
  } from '../../lib/hostSite.js'
  import {
    readOpsUnit,
    writeOpsUnit,
    horizonForUnit,
  } from '../../lib/horizon.js'
  import { LIVE_OPS_UNITS } from '../../mode.js'
  import OpsConfirm from '../../components/OpsConfirm.svelte'

  const OPS_UNITS = opsUnitsForSite(SITE_MODE)
  const showUnitSwitch = OPS_UNITS.length > 1

  let confirmOpen = false
  let pendingAction = 'restart'
  let msg = ''
  let msgKind = ''
  let confirmRef
  let unit = (() => {
    const saved = readOpsUnit(OPS_UNITS)
    return OPS_UNITS.includes(saved) ? saved : defaultOpsUnitForSite(SITE_MODE)
  })()

  $: status = $opsStatus
  $: visibleUnits = ((status && status.units) || []).filter(
    (u) => u && OPS_UNITS.includes(String(u.unit || u.name || '')),
  )
  $: canMutate = !!(
    status &&
    status.controls &&
    status.controls.start_stop &&
    (status.controls.mutable_units || []).includes(unit)
  )
  $: hzLabel = horizonForUnit(unit)
  $: unitTitle = SITE_MODE === 'live' ? 'Live unit' : 'Paper unit'
  $: needsTypedConfirm = !LIVE_OPS_UNITS.includes(unit)

  function selectUnit(u) {
    unit = u
    writeOpsUnit(u, OPS_UNITS)
    msg = ''
  }

  function openConfirm(action) {
    if ($opsInFlight || !canMutate) return
    pendingAction = action
    confirmOpen = true
    msg = ''
  }

  async function runAction(action) {
    if ($opsInFlight || !canMutate) return
    if (!needsTypedConfirm) {
      await onConfirm({ detail: { action, confirm: '', unit } })
      return
    }
    openConfirm(action)
  }

  async function onConfirm(e) {
    const { action, confirm } = e.detail
    opsInFlight.set(true)
    msg = 'Выполняется ' + action + '…'
    msgKind = 'pending'
    try {
      const r = await executeOpsCommand(action, confirm, { unit, mode: $tradingMode })
      if (!r.ok) {
        msg = 'Ошибка: ' + (r.error_ru || OPS_ERROR_RU[r.error] || r.error || 'ops_failed')
        msgKind = 'bad'
      } else {
        msg =
          'OK: ' +
          (r.data.action || action) +
          ' ' +
          (r.data.unit || unit) +
          (r.data.unit_status && r.data.unit_status.active_state
            ? ' → ' + r.data.unit_status.active_state
            : '')
        msgKind = 'ok'
        confirmOpen = false
        if (confirmRef) confirmRef.close()
      }
      await refreshCore()
    } catch {
      msg = 'Сеть / API недоступны'
      msgKind = 'bad'
    } finally {
      opsInFlight.set(false)
    }
  }
</script>

<section class="pm-surface" aria-label="Управление ботом">
  <div class="pm-section-head">
    <h2>Сервис бота</h2>
  </div>

  {#if showUnitSwitch}
    <div class="pm-hz" role="group" aria-label={unitTitle}>
      {#each OPS_UNITS as u}
        <button
          type="button"
          class="pm-hz__btn"
          class:is-on={unit === u}
          on:click={() => selectUnit(u)}
        >
          {horizonForUnit(u)}
        </button>
      {/each}
    </div>
  {/if}
  <p class="pm-meta mono" style="margin:0.35rem 0 0.75rem">{unit}</p>

  {#if visibleUnits.length}
    <p class="pm-meta" class:pm-ops-msg--ok={status.overall === 'active'} class:pm-ops-msg--bad={status.overall !== 'active'}>
      {#each visibleUnits as u, i}
        {#if i > 0} · {/if}
        {#if !u.ok}
          {u.unit}: n/a
        {:else}
          {u.unit} {u.active_state}{u.sub_state ? '/' + u.sub_state : ''}{#if u.main_pid != null}
            · pid {u.main_pid}{u.pid_alive ? '' : ' (dead)'}{/if}
        {/if}
      {/each}
    </p>
  {:else}
    <p class="pm-empty">Статус units недоступен (локально без systemd — ожидаемо).</p>
  {/if}

  {#if canMutate}
    <p class="pm-meta" style="margin-top:0.75rem">
      {#if needsTypedConfirm}
        Управление · {hzLabel}. Подтверждение строкой <code>START|STOP|RESTART {unit}</code> — с серверной
        проверкой. Confirm никогда не подставляется автоматически.
      {:else}
        Управление · {hzLabel}. Один клик (SSO) — без модалки и typed confirm, как AI Live toggle.
      {/if}
    </p>
    <div class="pm-ops-btns">
      <button type="button" class="pm-btn" disabled={$opsInFlight} on:click={() => runAction('start')}>
        Старт
      </button>
      <button type="button" class="pm-btn pm-btn--warn" disabled={$opsInFlight} on:click={() => runAction('stop')}>
        Стоп
      </button>
      <button
        type="button"
        class="pm-btn pm-btn--accent"
        disabled={$opsInFlight}
        on:click={() => runAction('restart')}
      >
        Перезапуск
      </button>
    </div>
  {:else}
    <p class="pm-meta" style="margin-top:0.75rem">Мутации недоступны для текущего unit / allowlist.</p>
  {/if}

  {#if msg}
    <p class="pm-ops-msg pm-ops-msg--{msgKind}">{msg}</p>
  {/if}
</section>

{#if needsTypedConfirm}
  <OpsConfirm
    bind:this={confirmRef}
    bind:open={confirmOpen}
    action={pendingAction}
    {unit}
  />
{/if}

<style>
  .mono {
    font-variant-numeric: tabular-nums;
  }
</style>
